import { RecurrencePattern, RecurrenceWeekday } from './recurrence-pattern';

const WEEKDAY_LABELS: Record<RecurrenceWeekday, string> = {
  mo: 'man',
  tu: 'tir',
  we: 'ons',
  th: 'tor',
  fr: 'fre',
  sa: 'lør',
  su: 'søn',
};

const WEEKDAY_ORDER: RecurrenceWeekday[] = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];

function formatUntilDate(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }
  return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Builds a Danish human-readable summary of a recurrence pattern.
 * rrule's .toText() has no Danish locale so we derive the summary directly from the typed object.
 *
 * Examples:
 *   "Gentages dagligt" (daily, no end)
 *   "Gentages hver 2. dag — slutter efter 5 gange"
 *   "Gentages ugentligt på man, ons — slutter 1. jun. 2026"
 *   "Gentages månedligt den 15. — slutter efter 10 gange"
 */
export function buildRecurrenceSummary(pattern: RecurrencePattern): string {
  const { freq, byweekday, bymonthday, count, until } = pattern;
  // ?? frem for destructuring-default: BFF'en serialiserer interval 1 som eksplicit null.
  const interval = pattern.interval ?? 1;

  let freqPart: string;
  switch (freq) {
    case 'daily':
      freqPart = interval === 1 ? 'dagligt' : `hver ${interval}. dag`;
      break;
    case 'weekly': {
      const dayLabels =
        byweekday && byweekday.length > 0
          ? [...byweekday].sort((a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b)).map(d => WEEKDAY_LABELS[d]).join(', ')
          : '';
      const basePart = interval === 1 ? 'ugentligt' : `hver ${interval}. uge`;
      freqPart = dayLabels ? `${basePart} på ${dayLabels}` : basePart;
      break;
    }
    case 'monthly': {
      const monthlyBase = interval === 1 ? 'månedligt' : `hver ${interval}. måned`;
      freqPart = bymonthday != null ? `${monthlyBase} den ${bymonthday}.` : monthlyBase;
      break;
    }
    default:
      freqPart = '';
  }

  let endPart: string;
  if (count != null) {
    endPart = ` — slutter efter ${count} ${count === 1 ? 'gang' : 'gange'}`;
  } else if (until) {
    endPart = ` — slutter ${formatUntilDate(until)}`;
  } else {
    endPart = '';
  }

  return `Gentages ${freqPart}${endPart}`;
}
